import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import type { Dashboard } from "../lib/types";
import { formatINR, formatPct, formatPoints } from "../lib/format";
import {
  Badge,
  EmptyState,
  ErrorBanner,
  Money,
  PageHeader,
  Panel,
  ProgressBar,
  Spinner,
  StatTile,
} from "../components/ui";
import { CapTracker } from "../components/CapTracker";
import { CreditLimits } from "../components/CreditLimits";
import {
  CardValueChart,
  CategoryBreakdownChart,
  MonthlySpendChart,
  RewardValueChart,
} from "../components/charts";

export default function DashboardPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Dashboard>("/analytics/dashboard")
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load the dashboard"));
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!data) return <Spinner />;

  const { summary, trend, byCategory, byCard, cardReturns, milestones, balances } = data;
  const hasData = summary.lifetimeTransactions > 0;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`${summary.monthLabel} · ${summary.activeCards} active card${summary.activeCards === 1 ? "" : "s"}`}
        actions={
          <>
            <Link to="/optimizer" className="btn-secondary">Which card?</Link>
            <Link to="/transactions" className="btn-primary">Add spend</Link>
          </>
        }
      />

      {!hasData && (
        <div className="mb-6">
          <EmptyState
            title="No transactions yet"
            hint="Add your cards, then log a few spends — points, milestones and the optimiser all build on your transaction history."
            action={
              <div className="flex gap-2">
                <Link to="/cards" className="btn-primary">Add a card</Link>
                <Link to="/transactions" className="btn-secondary">Log a spend</Link>
              </div>
            }
          />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Spend this month" value={formatINR(summary.monthSpend)} sub={`${summary.ytdTransactions} transactions YTD`} />
        <StatTile
          label="Points this month"
          value={formatPoints(summary.monthPoints)}
          sub={`Worth ${formatINR(summary.monthValue)} · ${formatPct(summary.monthReturnPct)} return`}
        />
        <StatTile
          label="Points on hand"
          value={formatPoints(summary.totalPoints)}
          sub={
            summary.pointsExpiringSoon > 0
              ? `Worth ${formatINR(summary.totalPointsValue)} · ${formatPoints(summary.pointsExpiringSoon)} expiring in ${summary.expiringSoonDays} days`
              : `Worth ${formatINR(summary.totalPointsValue)} across all programmes`
          }
          tone={summary.pointsExpiringSoon > 0 ? "warning" : "default"}
        />
        <StatTile
          label="Annual fees at stake"
          value={formatINR(summary.annualFeeExposure)}
          sub="Across your active cards"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Monthly spend" subtitle="Across all cards">
          {trend.some((t) => t.spend > 0) ? (
            <MonthlySpendChart data={trend} />
          ) : (
            <EmptyState title="Nothing to chart yet" hint="Log a few transactions to see your spending trend." />
          )}
        </Panel>

        <Panel title="Reward value earned" subtitle="Points earned each month, valued at your own rate">
          {trend.some((t) => t.value > 0) ? (
            <RewardValueChart data={trend} />
          ) : (
            <EmptyState title="No rewards yet" hint="Points are valued using the rate you set per programme." />
          )}
        </Panel>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Where this month went" subtitle="Spend by category">
          {byCategory.length > 0 ? (
            <>
              <CategoryBreakdownChart data={byCategory} />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ink-200">
                      <th className="th">Category</th>
                      <th className="th text-right">Spend</th>
                      <th className="th text-right">Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {byCategory.slice(0, 8).map((c) => (
                      <tr key={c.category}>
                        <td className="td">{c.label}</td>
                        <td className="td tnum text-right"><Money value={c.spend} /></td>
                        <td className="td tnum text-right text-ink-500">{formatPoints(c.points)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <EmptyState title="No spend this month" />
          )}
        </Panel>

        <Panel title="Reward value by card" subtitle="Lifetime, at your valuation">
          {cardReturns.length > 0 ? (
            <>
              <CardValueChart data={cardReturns} />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ink-200">
                      <th className="th">Card</th>
                      <th className="th text-right">Return</th>
                      <th className="th text-right">Net of fee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {cardReturns.map((c) => (
                      <tr key={c.cardId}>
                        <td className="td">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: c.color }} aria-hidden />
                            {c.cardName}
                          </span>
                        </td>
                        <td className="td tnum text-right">{formatPct(c.returnPct)}</td>
                        <td className={`td tnum text-right ${c.netOfFee < 0 ? "text-red-700" : "text-emerald-700"}`}>
                          {formatINR(c.netOfFee)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <EmptyState title="No cards yet" action={<Link to="/cards" className="btn-primary">Add a card</Link>} />
          )}
        </Panel>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel
          title="Milestones in progress"
          subtitle="Closest to completion first"
          actions={<Link to="/milestones" className="text-xs font-medium text-accent-700 underline">View all</Link>}
        >
          {milestones.length > 0 ? (
            <ul className="space-y-4">
              {milestones.map((m) => (
                <li key={m.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink-900">{m.name}</p>
                      <p className="truncate text-xs text-ink-500">
                        {m.cardName} · {m.rewardDescription}
                      </p>
                    </div>
                    <span className="tnum shrink-0 text-xs text-ink-500">
                      {formatINR(m.spent, { compact: true })} / {formatINR(m.threshold, { compact: true })}
                    </span>
                  </div>
                  <div className="mt-2">
                    <ProgressBar pct={m.pct} achieved={m.achieved} />
                  </div>
                  <p className="mt-1.5 text-xs text-ink-500">
                    <Money value={m.remaining} /> to go · {m.daysLeft} day{m.daysLeft === 1 ? "" : "s"} left
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No milestones in progress" hint="Milestones are added automatically for catalogue cards, or you can create your own." />
          )}
        </Panel>

        <Panel
          title="This statement cycle"
          subtitle="Spend per card in the current cycle"
        >
          {byCard.length > 0 ? (
            <ul className="divide-y divide-ink-100">
              {byCard.map((c) => (
                <li key={c.cardId} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ background: c.color }} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink-900">{c.cardName}</p>
                    <p className="text-xs text-ink-500">{c.statementCycle} · due on the {ordinal(c.dueDay)}</p>
                  </div>
                  <div className="text-right">
                    <p className="tnum text-sm font-medium text-ink-900">{formatINR(c.cycleSpend)}</p>
                    <p className="tnum text-xs text-ink-500">{formatPoints(c.points)} pts this month</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="No cards yet" />
          )}
        </Panel>
      </div>

      <CreditLimits className="mt-6" />

      <CapTracker className="mt-6" />

      {balances.length > 0 && balances.some((b) => b.balance > 0) && (
        <div className="mt-6">
          <Panel
            title="Points balances"
            subtitle="FIFO expiry, valued at your own rate"
            actions={<Link to="/points" className="text-xs font-medium text-accent-700 underline">Manage</Link>}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {balances
                .filter((b) => b.balance > 0)
                .map((b) => (
                  <div key={b.currencyId} className="rounded-lg border border-ink-200 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-ink-900">{b.currencyName}</p>
                      {b.expiringSoon > 0 && <Badge tone="warning">Expiring soon</Badge>}
                    </div>
                    <p className="tnum mt-2 text-xl font-semibold text-ink-950">{formatPoints(b.balance)}</p>
                    <p className="mt-1 text-xs text-ink-500">
                      Worth {formatINR(b.value)} at ₹{b.valuePerPoint}/pt
                    </p>
                    {b.expiringSoon > 0 && (
                      <p className="mt-1 text-xs text-amber-700">
                        {formatPoints(b.expiringSoon)} expiring within {summary.expiringSoonDays} days
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
